import React, { useMemo, useState, useEffect } from "react";
import { FilterRow } from "./filter-row";
import { AddFilterRow } from "./add-filter-row";
import { X } from "lucide-react";
import { FilterCondition, FilterConfig } from "@/interfaces/filter.interface";
import { FilterOperator } from "@/constants/filter.constant";

/**
 * Props for the FilterPanel component
 */
interface FilterPanelProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Array of filter conditions */
  conditions: FilterCondition[];
  /** Filter configuration object */
  config: FilterConfig;
  /** Callback fired when a condition changes */
  onConditionChange: (condition: FilterCondition) => void;
  /** Callback fired when a condition is removed */
  onConditionRemove: (conditionId: string) => void;
  /** Callback fired when a condition is added */
  onConditionAdd: () => void;
  /** Callback fired when pending filter changes */
  onPendingFilterChange?: (pending: Partial<FilterCondition> | null) => void;
  /** Callback fired when panel is closed */
  onClose?: () => void;
}

/**
 * FilterPanel component displays a collapsible panel with filter rows for building
 * complex filter conditions. Manages filter validation, field usage tracking, and
 * maximum filter limits. Renders FilterRow components for each condition.
 * 
 * @component
 * @param {FilterPanelProps} props - Component props
 * @param {boolean} props.isOpen - Whether panel is open
 * @param {FilterCondition[]} props.conditions - Filter conditions
 * @param {FilterConfig} props.config - Filter configuration
 * @param {(condition: FilterCondition) => void} props.onConditionChange - Condition change callback
 * @param {(conditionId: string) => void} props.onConditionRemove - Condition remove callback
 * @param {() => void} props.onConditionAdd - Condition add callback
 * @param {(pending: Partial<FilterCondition> | null) => void} [props.onPendingFilterChange] - Pending filter change callback
 * @param {() => void} [props.onClose] - Close callback
 * @returns {JSX.Element | null} Filter panel with rows or null if not open
 * 
 * @example
 * ```tsx
 * <FilterPanel
 *   isOpen={isOpen}
 *   conditions={filters}
 *   config={filterConfig}
 *   onConditionChange={handleChange}
 * />
 * ```
 */
export const FilterPanel: React.FC<FilterPanelProps> = ({
  isOpen,
  conditions,
  config,
  onConditionChange,
  onConditionRemove,
  onConditionAdd,
  onPendingFilterChange,
  onClose,
}) => {
  const maxFilters = config.maxFilters ?? 2;
  const allowDuplicateFields = config.allowDuplicateFields ?? false;
  const [showAddRow, setShowAddRow] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setShowAddRow(true);
    }
  }, [isOpen]);
  
  const usedFields = useMemo(() => {
    if (allowDuplicateFields) return [];
    return conditions.map((c) => c.field).filter(Boolean);
  }, [conditions, allowDuplicateFields]);

  const canAddMore = conditions.length < maxFilters;
  const maxReached = conditions.length >= maxFilters;

  const firstFilterValid = useMemo(() => {
    if (conditions.length === 0) return true;
    const first = conditions[0];
    const field = config.fields.find((f) => f.id === first.field);
    return (
      !!field &&
      !!first.operator &&
      first.value.trim() !== ""
    );
  }, [conditions, config.fields]);

  const lastFilterValid = useMemo(() => {
    if (conditions.length === 0) return true;
    const last = conditions[conditions.length - 1];
    const field = config.fields.find((f) => f.id === last.field);
    return (
      !!field &&
      !!last.operator &&
      last.value.trim() !== ""
    );
  }, [conditions, config.fields]);

  const canAdd = conditions.length === 0
      ? canAddMore && !maxReached
      : firstFilterValid && canAddMore && !maxReached;

  const handleFieldChange = (conditionId: string, fieldId: string) => {
    const condition = conditions.find((c) => c.id === conditionId);
    if (!condition) return;
    const updated: FilterCondition = {
      ...condition,
      field: fieldId,
      value: "",
      values: [],
      operator: FilterOperator.NONE,
    };
    onConditionChange(updated);
  };

  const handleOperatorChange = (conditionId: string, operator: FilterOperator) => {
    const condition = conditions.find((c) => c.id === conditionId);
    if (!condition) return;
    const updated: FilterCondition = {
      ...condition,
      operator,
    };
    onConditionChange(updated);
  };

  const handleValueChange = (conditionId: string, valueOrArray: string | string[]) => {
    const condition = conditions.find((c) => c.id === conditionId);
    if (!condition) return;

    let values: string[] = [];
    let value = "";

    if (Array.isArray(valueOrArray)) {
      values = valueOrArray;
      value = values.join(",");
    } else {
      value = valueOrArray ?? "";
      values = value === "" ? [] : value.split(",").map((v) => v.trim()).filter(Boolean);
    }

    const updated: FilterCondition = {
      ...condition,
      value,
      values,
    };

    onConditionChange(updated);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg border border-border p-4 pb-6 space-y-3 relative">
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          className="absolute top-1 right-4 h-6 w-6 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          aria-label="Close filter panel"
        >
          <X className="h-4 w-4" />
        </button>
      )}

      {conditions.map((condition, index) => {
        const otherUsedFields = usedFields.filter((f) => f !== condition.field);
        const isLastCondition = index === conditions.length - 1;
        const shouldShowPlus = isLastCondition && !showAddRow && canAddMore && !maxReached && lastFilterValid;

        return (
          <FilterRow
            key={condition.id}
            condition={condition}
            availableFields={config.fields}
            usedFields={otherUsedFields}
            index={index}
            onFieldChange={(fieldId) => handleFieldChange(condition.id, fieldId)}
            onOperatorChange={(operator) => handleOperatorChange(condition.id, operator)}
            onValueChange={(valueOrArray) => handleValueChange(condition.id, valueOrArray)}
            onRemove={() => onConditionRemove(condition.id)}
            onAdd={() => {
              setShowAddRow(true);
            }}
            canAdd={shouldShowPlus}
            maxReached={maxReached}
          />
        );
      })}
      
      {canAddMore && showAddRow && (
        <AddFilterRow
          availableFields={config.fields}
          usedFields={usedFields}
          onAdd={(condition) => {
            onConditionChange(condition);
            onConditionAdd();
            setShowAddRow(true);
          }}
          onPendingFilterChange={onPendingFilterChange}
          onRemoveLast={() => {
            if (conditions.length > 0) {
              const lastCondition = conditions[conditions.length - 1];
              onConditionRemove(lastCondition.id);
            }
          }}
          onRemoveRow={() => {
            setShowAddRow(conditions.length == 0 ? true : false);
            if (onPendingFilterChange) {
              onPendingFilterChange(null);
            }
          }}
          canAdd={canAdd}
          maxReached={maxReached}
          isLastRow={conditions.length + 1 >= maxFilters}
          disableRemove={conditions.length === 0}
          />
      )}
        </div>
  );
};

